import { Core, Decorative } from "@faire/design-tokens";
import {
  IProductSpotlightGroup,
  getProductSpotlightCards,
} from "@faire/retailer-visitor-shared/components/BrandProductSpotlight/utils";
// eslint-disable-next-line import/no-unassigned-import
import "@faire/retailer-visitor-shared/components/slick.css";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { trackBrandLeadsBrandsPageSpotlightCarouselView } from "@faire/web-api--source/events/brandLeads/view/brandsPageSpotlightCarousel";
import times from "lodash/times";
import React, { useCallback, useState, useRef } from "react";
import Slider from "react-slick";
import styled from "styled-components";

interface IProps {
  initialSlide?: number;
  isPersonalized?: boolean;
  showCaseStudies?: boolean;
  title?: string;
  onView?: () => void;
}

export const BrandProductSpotlight: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { isCanadian, isAmerican } = useNationality();
  const isNorthAmerican = isCanadian || isAmerican;

  const [currentIndex, setCurrentIndex] = useState<number>(
    props.initialSlide ?? 0
  );
  const cardSliderRef = useRef<Slider>(null);
  const { isMobile, isTabletAndAbove } = useViewport();
  const isSSR = useIsSSR();

  const onClickNextSlideHandler = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      setCurrentIndex(currentIndex + 1);
      cardSliderRef.current?.slickNext();
    },
    [currentIndex]
  );

  const onClickPrevSlideHandler = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      cardSliderRef.current?.slickPrev();
      setCurrentIndex(currentIndex - 1);
    },
    [currentIndex]
  );

  const onCardClickHandler = useCallback((index: number) => {
    setCurrentIndex(index);
    cardSliderRef.current?.slickGoTo(index);
  }, []);

  const handleSliderChangeHandler = useCallback(
    (_: number, newIndex: number) => {
      setCurrentIndex(newIndex);
      const key = getProductSpotlightCards(strictLocalize, isNorthAmerican)[
        currentIndex
      ]?.eventKey;
      if (key) {
        trackBrandLeadsBrandsPageSpotlightCarouselView(key);
      }
    },
    [currentIndex, strictLocalize, isNorthAmerican]
  );

  const viewRef = useDoActionWhenVisible(props.onView);

  const renderCardContentHandler = useCallback(
    (card: IProductSpotlightGroup, index: number, isMobile: boolean) => {
      return (
        <Card $isSelected={index === currentIndex}>
          <ImageContainer>
            {isMobile ? <Spacer size="4x" /> : null}
            <img
              src={
                isMobile
                  ? optimizeImageUrl(
                      {
                        url: card.image,
                      },
                      isSSR,
                      {
                        width: 317,
                        height: 181,
                      }
                    )
                  : optimizeImageUrl(
                      {
                        url: card.image,
                      },
                      isSSR,
                      {
                        width: 410,
                        height: 258,
                      }
                    )
              }
              style={{
                width: isMobile ? 317 : 410,
                height: isMobile ? 181 : 258,
              }}
            />
          </ImageContainer>
          <TitlesContainer>
            <Spacer size="2x" />
            <CardTitle
              variant={
                isMobile ? "subheadingSansMedium" : "subheadingSansRegular"
              }
              align={isMobile ? "left" : "center"}
            >
              {card.title}
            </CardTitle>
            {isMobile ? null : <Spacer size="1x" />}
            <SubtitleContainer align={isMobile ? "left" : "center"}>
              {card.subtitle}
            </SubtitleContainer>
            <Spacer size="2x" />
          </TitlesContainer>
        </Card>
      );
    },
    [currentIndex, isSSR]
  );

  const productSpotlightCards = getProductSpotlightCards(
    strictLocalize,
    isNorthAmerican
  );
  return (
    <OuterContainer
      ref={props.onView ? viewRef : undefined}
      $showCaseStudies={props.showCaseStudies}
      $isPersonalized={props.isPersonalized}
    >
      <Container>
        <Spacer size={isMobile ? "4x" : "10x"} />
        <Typography
          variant={{
            mobileAndAbove: "pageHeaderSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
            desktopAndAbove: "displayLSerifRegular",
          }}
          align={isMobile ? "left" : "center"}
          style={{ marginLeft: isMobile ? getSpacing("3x") : 0 }}
        >
          {props.title ?? (
            <StrictLocalMsg
              defaultMessage="A simple, easy-to-use B2B platform"
              description={{
                text: "Header for Faire spotlight section describe Faire platform. Keep term B2B in English",
              }}
            />
          )}
        </Typography>
        <Spacer size={isMobile ? "2x" : "9x"} />
        <SlideControlWrapper>
          {isTabletAndAbove ? (
            <SlideControlButtonsWrapper>
              <SlideControlButton
                variant="secondary"
                onClick={(e) => onClickPrevSlideHandler(e)}
              >
                <Arrow width="14px" height="8px" direction="left" />
              </SlideControlButton>
              {/* Spacer is width of card content(482px) minus button width(32px) */}
              <Spacer width={450} />

              <SlideControlButton
                variant="secondary"
                onClick={(e) => onClickNextSlideHandler(e)}
              >
                <Arrow width="14px" height="8px" direction="right" />
              </SlideControlButton>
            </SlideControlButtonsWrapper>
          ) : null}
          <StyledSlider
            ref={cardSliderRef}
            arrows={false}
            variableWidth
            beforeChange={handleSliderChangeHandler}
            infinite
            centerMode
          >
            {productSpotlightCards.map((card, index) => (
              <div key={index}>
                <CardContainer onClick={() => onCardClickHandler(index)}>
                  {renderCardContentHandler(card, index, isMobile)}
                </CardContainer>
              </div>
            ))}
          </StyledSlider>
        </SlideControlWrapper>
      </Container>
      <Spacer size={isMobile ? "2x" : "6x"} />
      <ProgressDots
        progress={currentIndex}
        total={productSpotlightCards.length}
      />
      <Spacer size={isMobile ? "3x" : "9x"} />
    </OuterContainer>
  );
};

const OuterContainer = styled(Column).attrs({ align: "center" })<{
  $isPersonalized?: boolean;
  $showCaseStudies?: boolean;
}>`
  background-color: ${(props) =>
    // eslint-disable-next-line no-nested-ternary
    props.$isPersonalized
      ? Core.surface.muted
      : props.$showCaseStudies
        ? Core.surface.inverse
        : Decorative.tint.neutral};

  ${responsiveMediaQueries.mobile} {
    background-color: ${({ $isPersonalized }) =>
      $isPersonalized ? Core.surface.muted : Decorative.tint.neutral};
  }
`;

const Container = styled(Column)`
  width: 100%;

  ${responsiveMediaQueries.mobile} {
    min-height: 360px;
  }
`;

const CardContainer = styled.div`
  position: relative;
  padding: 0 15px 15px 15px;

  ${responsiveMediaQueries.mobile} {
    padding: 0 3px;
  }

  z-index: 1;
`;

const SubtitleContainer = styled(Typography).attrs({
  variant: "paragraphSansRegular",
})`
  width: 269px;
`;

const CardTitle = styled(Typography)`
  width: 269px;
`;

const ImageContainer = styled(Column).attrs({
  justify: "center",
  align: "center",
})`
  width: 100%;
  height: 294px;
  padding: 36px 36px 0 36px;
  background-color: ${Core.surface.muted};

  ${responsiveMediaQueries.mobile} {
    height: 181px;
    padding: 0;
  }
`;

const TitlesContainer = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  width: 100%;
  background-color: white;
  border-top: 1px solid ${Core.border.muted};

  ${responsiveMediaQueries.mobile} {
    height: 147px;
    border-radius: 0px 0px 4px 4px;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    height: 127px;
  }
`;

const Card = styled(Column)<{
  $isSelected: boolean;
}>`
  opacity: ${(props) => (props.$isSelected ? "100%" : "30%")};
  transition: opacity 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }

  width: 482px;
  background-color: ${Core.surface.muted};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    border: 1px solid ${Core.border.muted};
    border-radius: 4px;
    height: 328px;
  }

  z-index: 1;

  ${responsiveMediaQueries.tabletAndAbove} {
    height: 421px;
    border: 1px solid ${Core.border.muted};
    filter: ${(props) =>
      props.$isSelected ? "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))" : ""};
  }
`;

const StyledSlider = styled(Slider)`
  ${responsiveMediaQueries.mobile} {
    height: 328px;
    min-width: 317px;
  }
`;
const SlideControlWrapper = styled.div`
  position: relative;
`;

const SlideControlButtonsWrapper = styled(Row).attrs({
  justify: "center",
  align: "center",
})`
  position: absolute;
  top: 200px;
  z-index: 2;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const SlideControlButton = styled(Button)`
  padding: 0px;
  height: 35px;
  width: 35px;
  cursor: pointer;
  border-radius: 50%;
  border: 1px solid ${Core.border.muted};
`;

const Dot = styled.div<{ $color?: string }>`
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || Core.surface.subdued};
  margin: 3.5px;

  ${responsiveMediaQueries.mobile} {
    height: 5px;
    width: 5px;
    margin: 2.5px;
  }
`;

const ProgressDots: React.FunctionComponent<{
  progress: number;
  total: number;
}> = ({ progress, total }) => (
  <Row>
    {times(total, (num) => (
      <Dot
        key={num}
        color={num === progress ? Core.surface.enabled : undefined}
      />
    ))}
  </Row>
);
